/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * Noted API — collaborative human-made music platform
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  AdminAddSongFileBody,
  AdminCreateVersionBody,
  AdminListCommitsParams,
  AdminPreviewVersionMixBody,
  AdminPreviewVersionMixResponse,
  AdminSetCommitStatusBody,
  AdminStats,
  AdvancePhaseBody,
  Comment,
  CommitDetail,
  CommitDraft,
  CommitSummary,
  CreateDraftBody,
  CreateRoundBody,
  CreateSongBody,
  CreateSongCreditBody,
  CreditEntry,
  CurrentUser,
  FeaturedSong,
  HealthStatus,
  ListCommitsForRoundParams,
  ListCommitsParams,
  ListRisingCommitsParams,
  ListSongsParams,
  LogDownload200,
  LogDownloadBody,
  NotificationFeed,
  PostCommentBody,
  Profile,
  PublicStats,
  ReorderSongCreditsBody,
  ReportComment201,
  ReportCommentBody,
  ReportedComment,
  RequestUploadUrlBody,
  RequestUploadUrlResponse,
  Round,
  Song,
  SongCredit,
  SongCreditWithSong,
  SongDetail,
  SongFile,
  SubmitCommitBody,
  SubmitDraftBody,
  UpdateDraftBody,
  UpdateMeBody,
  UpdateRoundBody,
  UpdateSongBody,
  UpdateSongCreditBody,
  VersionWithMerges,
  VoteResult,
} from "./api.schemas";

import { customFetch } from "../custom-fetch";
import type { ErrorType, BodyType } from "../custom-fetch";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * @summary Health check
 */
export const getHealthCheckUrl = () => {
  return `/api/healthz`;
};

export const healthCheck = async (
  options?: RequestInit,
): Promise<HealthStatus> => {
  return customFetch<HealthStatus>(getHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getHealthCheckQueryKey = () => {
  return [`/api/healthz`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({
    signal,
  }) => healthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthCheck>>
>;
export type HealthCheckQueryError = ErrorType<unknown>;

/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Current user profile (null if unauthenticated)
 */
export const getGetCurrentUserUrl = () => {
  return `/api/me`;
};

export const getCurrentUser = async (
  options?: RequestInit,
): Promise<CurrentUser> => {
  return customFetch<CurrentUser>(getGetCurrentUserUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetCurrentUserQueryKey = () => {
  return [`/api/me`] as const;
};

export const getGetCurrentUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCurrentUserQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({
    signal,
  }) => getCurrentUser({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetCurrentUserQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCurrentUser>>
>;
export type GetCurrentUserQueryError = ErrorType<unknown>;

/**
 * @summary Current user profile (null if unauthenticated)
 */

export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetCurrentUserQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update the current user's preferences (e.g. email digest opt-out)
 */
export const getUpdateMeUrl = () => {
  return `/api/me`;
};

export const updateMe = async (
  updateMeBody: UpdateMeBody,
  options?: RequestInit,
): Promise<Profile> => {
  return customFetch<Profile>(getUpdateMeUrl(), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateMeBody),
  });
};

export const getUpdateMeMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMe>>,
    TError,
    { data: BodyType<UpdateMeBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMe>>,
  TError,
  { data: BodyType<UpdateMeBody> },
  TContext
> => {
  const mutationKey = ["updateMe"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMe>>,
    { data: BodyType<UpdateMeBody> }
  > = (props) => {
    const { data } = props ?? {};

    return updateMe(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMeMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMe>>
>;
export type UpdateMeMutationBody = BodyType<UpdateMeBody>;
export type UpdateMeMutationError = ErrorType<unknown>;

/**
 * @summary Update the current user's preferences (e.g. email digest opt-out)
 */
export const useUpdateMe = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMe>>,
    TError,
    { data: BodyType<UpdateMeBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateMe>>,
  TError,
  { data: BodyType<UpdateMeBody> },
  TContext
> => {
  return useMutation(getUpdateMeMutationOptions(options));
};

/**
 * @summary Recent notifications for the current user (auth required)
 */
export const getListMyNotificationsUrl = () => {
  return `/api/me/notifications`;
};

export const listMyNotifications = async (
  options?: RequestInit,
): Promise<NotificationFeed> => {
  return customFetch<NotificationFeed>(getListMyNotificationsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListMyNotificationsQueryKey = () => {
  return [`/api/me/notifications`] as const;
};

export const getListMyNotificationsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyNotifications>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyNotifications>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMyNotificationsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyNotifications>>
  > = ({ signal }) => listMyNotifications({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyNotifications>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyNotificationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyNotifications>>
>;
export type ListMyNotificationsQueryError = ErrorType<unknown>;

/**
 * @summary Recent notifications for the current user (auth required)
 */

export function useListMyNotifications<
  TData = Awaited<ReturnType<typeof listMyNotifications>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyNotifications>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyNotificationsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Mark every unread notification as read
 */
export const getMarkAllNotificationsReadUrl = () => {
  return `/api/me/notifications/read-all`;
};

export const markAllNotificationsRead = async (
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getMarkAllNotificationsReadUrl(), {
    ...options,
    method: "POST",
  });
};

export const getMarkAllNotificationsReadMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markAllNotificationsRead>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["markAllNotificationsRead"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markAllNotificationsRead>>,
    void
  > = () => {
    return markAllNotificationsRead(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkAllNotificationsReadMutationResult = NonNullable<
  Awaited<ReturnType<typeof markAllNotificationsRead>>
>;

export type MarkAllNotificationsReadMutationError = ErrorType<unknown>;

/**
 * @summary Mark every unread notification as read
 */
export const useMarkAllNotificationsRead = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markAllNotificationsRead>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getMarkAllNotificationsReadMutationOptions(options));
};

/**
 * @summary Mark a single notification as read
 */
export const getMarkNotificationReadUrl = (id: string) => {
  return `/api/me/notifications/${id}/read`;
};

export const markNotificationRead = async (
  id: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getMarkNotificationReadUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getMarkNotificationReadMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markNotificationRead>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markNotificationRead>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["markNotificationRead"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markNotificationRead>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return markNotificationRead(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkNotificationReadMutationResult = NonNullable<
  Awaited<ReturnType<typeof markNotificationRead>>
>;

export type MarkNotificationReadMutationError = ErrorType<unknown>;

/**
 * @summary Mark a single notification as read
 */
export const useMarkNotificationRead = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markNotificationRead>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof markNotificationRead>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getMarkNotificationReadMutationOptions(options));
};

/**
 * @summary List songs
 */
export const getListSongsUrl = (params?: ListSongsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/songs?${stringifiedParams}`
    : `/api/songs`;
};

export const listSongs = async (
  params?: ListSongsParams,
  options?: RequestInit,
): Promise<Song[]> => {
  return customFetch<Song[]>(getListSongsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListSongsQueryKey = (params?: ListSongsParams) => {
  return [`/api/songs`, ...(params ? [params] : [])] as const;
};

export const getListSongsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSongs>>,
  TError = ErrorType<unknown>,
>(
  params?: ListSongsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listSongs>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSongsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSongs>>> = ({
    signal,
  }) => listSongs(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSongs>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListSongsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSongs>>
>;
export type ListSongsQueryError = ErrorType<unknown>;

/**
 * @summary List songs
 */

export function useListSongs<
  TData = Awaited<ReturnType<typeof listSongs>>,
  TError = ErrorType<unknown>,
>(
  params?: ListSongsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listSongs>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListSongsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get the currently featured song (landing page hero)
 */
export const getGetFeaturedSongUrl = () => {
  return `/api/songs/featured`;
};

export const getFeaturedSong = async (
  options?: RequestInit,
): Promise<FeaturedSong> => {
  return customFetch<FeaturedSong>(getGetFeaturedSongUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetFeaturedSongQueryKey = () => {
  return [`/api/songs/featured`] as const;
};

export const getGetFeaturedSongQueryOptions = <
  TData = Awaited<ReturnType<typeof getFeaturedSong>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFeaturedSong>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFeaturedSongQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFeaturedSong>>> = ({
    signal,
  }) => getFeaturedSong({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getFeaturedSong>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetFeaturedSongQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFeaturedSong>>
>;
export type GetFeaturedSongQueryError = ErrorType<unknown>;

/**
 * @summary Get the currently featured song (landing page hero)
 */

export function useGetFeaturedSong<
  TData = Awaited<ReturnType<typeof getFeaturedSong>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFeaturedSong>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetFeaturedSongQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getGetSongUrl = (songId: string) => {
  return `/api/songs/${songId}`;
};

export const getSong = async (
  songId: string,
  options?: RequestInit,
): Promise<SongDetail> => {
  return customFetch<SongDetail>(getGetSongUrl(songId), {
    ...options,
    method: "GET",
  });
};

export const getGetSongQueryKey = (songId: string) => {
  return [`/api/songs/${songId}`] as const;
};

export const getGetSongQueryOptions = <
  TData = Awaited<ReturnType<typeof getSong>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getSong>>, TError, TData>;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSongQueryKey(songId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSong>>> = ({
    signal,
  }) => getSong(songId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!songId,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getSong>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetSongQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSong>>
>;
export type GetSongQueryError = ErrorType<unknown>;

export function useGetSong<
  TData = Awaited<ReturnType<typeof getSong>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getSong>>, TError, TData>;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetSongQueryOptions(songId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getGetSongBySlugUrl = (slug: string) => {
  return `/api/songs/by-slug/${slug}`;
};

export const getSongBySlug = async (
  slug: string,
  options?: RequestInit,
): Promise<SongDetail> => {
  return customFetch<SongDetail>(getGetSongBySlugUrl(slug), {
    ...options,
    method: "GET",
  });
};

export const getGetSongBySlugQueryKey = (slug: string) => {
  return [`/api/songs/by-slug/${slug}`] as const;
};

export const getGetSongBySlugQueryOptions = <
  TData = Awaited<ReturnType<typeof getSongBySlug>>,
  TError = ErrorType<unknown>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSongBySlug>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSongBySlugQueryKey(slug);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSongBySlug>>> = ({
    signal,
  }) => getSongBySlug(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getSongBySlug>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetSongBySlugQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSongBySlug>>
>;
export type GetSongBySlugQueryError = ErrorType<unknown>;

export function useGetSongBySlug<
  TData = Awaited<ReturnType<typeof getSongBySlug>>,
  TError = ErrorType<unknown>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSongBySlug>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetSongBySlugQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getListSongFilesUrl = (songId: string) => {
  return `/api/songs/${songId}/files`;
};

export const listSongFiles = async (
  songId: string,
  options?: RequestInit,
): Promise<SongFile[]> => {
  return customFetch<SongFile[]>(getListSongFilesUrl(songId), {
    ...options,
    method: "GET",
  });
};

export const getListSongFilesQueryKey = (songId: string) => {
  return [`/api/songs/${songId}/files`] as const;
};

export const getListSongFilesQueryOptions = <
  TData = Awaited<ReturnType<typeof listSongFiles>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listSongFiles>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSongFilesQueryKey(songId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSongFiles>>> = ({
    signal,
  }) => listSongFiles(songId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!songId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listSongFiles>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListSongFilesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSongFiles>>
>;
export type ListSongFilesQueryError = ErrorType<unknown>;

export function useListSongFiles<
  TData = Awaited<ReturnType<typeof listSongFiles>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listSongFiles>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListSongFilesQueryOptions(songId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getListRoundsForSongUrl = (songId: string) => {
  return `/api/songs/${songId}/rounds`;
};

export const listRoundsForSong = async (
  songId: string,
  options?: RequestInit,
): Promise<Round[]> => {
  return customFetch<Round[]>(getListRoundsForSongUrl(songId), {
    ...options,
    method: "GET",
  });
};

export const getListRoundsForSongQueryKey = (songId: string) => {
  return [`/api/songs/${songId}/rounds`] as const;
};

export const getListRoundsForSongQueryOptions = <
  TData = Awaited<ReturnType<typeof listRoundsForSong>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listRoundsForSong>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListRoundsForSongQueryKey(songId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listRoundsForSong>>
  > = ({ signal }) => listRoundsForSong(songId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!songId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listRoundsForSong>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListRoundsForSongQueryResult = NonNullable<
  Awaited<ReturnType<typeof listRoundsForSong>>
>;
export type ListRoundsForSongQueryError = ErrorType<unknown>;

export function useListRoundsForSong<
  TData = Awaited<ReturnType<typeof listRoundsForSong>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listRoundsForSong>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListRoundsForSongQueryOptions(songId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getListVersionsForSongUrl = (songId: string) => {
  return `/api/songs/${songId}/versions`;
};

export const listVersionsForSong = async (
  songId: string,
  options?: RequestInit,
): Promise<VersionWithMerges[]> => {
  return customFetch<VersionWithMerges[]>(getListVersionsForSongUrl(songId), {
    ...options,
    method: "GET",
  });
};

export const getListVersionsForSongQueryKey = (songId: string) => {
  return [`/api/songs/${songId}/versions`] as const;
};

export const getListVersionsForSongQueryOptions = <
  TData = Awaited<ReturnType<typeof listVersionsForSong>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listVersionsForSong>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListVersionsForSongQueryKey(songId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listVersionsForSong>>
  > = ({ signal }) =>
    listVersionsForSong(songId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!songId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listVersionsForSong>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListVersionsForSongQueryResult = NonNullable<
  Awaited<ReturnType<typeof listVersionsForSong>>
>;
export type ListVersionsForSongQueryError = ErrorType<unknown>;

export function useListVersionsForSong<
  TData = Awaited<ReturnType<typeof listVersionsForSong>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listVersionsForSong>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListVersionsForSongQueryOptions(songId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary All third-party credits across every song (for the global /licenses page)
 */
export const getListAllCreditsUrl = () => {
  return `/api/credits`;
};

export const listAllCredits = async (
  options?: RequestInit,
): Promise<SongCreditWithSong[]> => {
  return customFetch<SongCreditWithSong[]>(getListAllCreditsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListAllCreditsQueryKey = () => {
  return [`/api/credits`] as const;
};

export const getListAllCreditsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAllCredits>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listAllCredits>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAllCreditsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAllCredits>>> = ({
    signal,
  }) => listAllCredits({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAllCredits>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListAllCreditsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAllCredits>>
>;
export type ListAllCreditsQueryError = ErrorType<unknown>;

/**
 * @summary All third-party credits across every song (for the global /licenses page)
 */

export function useListAllCredits<
  TData = Awaited<ReturnType<typeof listAllCredits>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listAllCredits>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListAllCreditsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getListCreditsUrl = (songId: string) => {
  return `/api/songs/${songId}/credits`;
};

export const listCredits = async (
  songId: string,
  options?: RequestInit,
): Promise<CreditEntry[]> => {
  return customFetch<CreditEntry[]>(getListCreditsUrl(songId), {
    ...options,
    method: "GET",
  });
};

export const getListCreditsQueryKey = (songId: string) => {
  return [`/api/songs/${songId}/credits`] as const;
};

export const getListCreditsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCredits>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCredits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCreditsQueryKey(songId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCredits>>> = ({
    signal,
  }) => listCredits(songId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!songId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listCredits>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListCreditsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCredits>>
>;
export type ListCreditsQueryError = ErrorType<unknown>;

export function useListCredits<
  TData = Awaited<ReturnType<typeof listCredits>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCredits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCreditsQueryOptions(songId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getGetRoundUrl = (roundId: string) => {
  return `/api/rounds/${roundId}`;
};

export const getRound = async (
  roundId: string,
  options?: RequestInit,
): Promise<Round> => {
  return customFetch<Round>(getGetRoundUrl(roundId), {
    ...options,
    method: "GET",
  });
};

export const getGetRoundQueryKey = (roundId: string) => {
  return [`/api/rounds/${roundId}`] as const;
};

export const getGetRoundQueryOptions = <
  TData = Awaited<ReturnType<typeof getRound>>,
  TError = ErrorType<unknown>,
>(
  roundId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRound>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRoundQueryKey(roundId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRound>>> = ({
    signal,
  }) => getRound(roundId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!roundId,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getRound>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetRoundQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRound>>
>;
export type GetRoundQueryError = ErrorType<unknown>;

export function useGetRound<
  TData = Awaited<ReturnType<typeof getRound>>,
  TError = ErrorType<unknown>,
>(
  roundId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRound>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetRoundQueryOptions(roundId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getListCommitsForRoundUrl = (
  roundId: string,
  params?: ListCommitsForRoundParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/rounds/${roundId}/commits?${stringifiedParams}`
    : `/api/rounds/${roundId}/commits`;
};

export const listCommitsForRound = async (
  roundId: string,
  params?: ListCommitsForRoundParams,
  options?: RequestInit,
): Promise<CommitSummary[]> => {
  return customFetch<CommitSummary[]>(
    getListCommitsForRoundUrl(roundId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListCommitsForRoundQueryKey = (
  roundId: string,
  params?: ListCommitsForRoundParams,
) => {
  return [
    `/api/rounds/${roundId}/commits`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListCommitsForRoundQueryOptions = <
  TData = Awaited<ReturnType<typeof listCommitsForRound>>,
  TError = ErrorType<unknown>,
>(
  roundId: string,
  params?: ListCommitsForRoundParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCommitsForRound>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListCommitsForRoundQueryKey(roundId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listCommitsForRound>>
  > = ({ signal }) =>
    listCommitsForRound(roundId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!roundId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listCommitsForRound>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListCommitsForRoundQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCommitsForRound>>
>;
export type ListCommitsForRoundQueryError = ErrorType<unknown>;

export function useListCommitsForRound<
  TData = Awaited<ReturnType<typeof listCommitsForRound>>,
  TError = ErrorType<unknown>,
>(
  roundId: string,
  params?: ListCommitsForRoundParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCommitsForRound>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCommitsForRoundQueryOptions(
    roundId,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List commits (filter by round or song)
 */
export const getListCommitsUrl = (params?: ListCommitsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/commits?${stringifiedParams}`
    : `/api/commits`;
};

export const listCommits = async (
  params?: ListCommitsParams,
  options?: RequestInit,
): Promise<CommitSummary[]> => {
  return customFetch<CommitSummary[]>(getListCommitsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListCommitsQueryKey = (params?: ListCommitsParams) => {
  return [`/api/commits`, ...(params ? [params] : [])] as const;
};

export const getListCommitsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCommits>>,
  TError = ErrorType<unknown>,
>(
  params?: ListCommitsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCommits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCommitsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCommits>>> = ({
    signal,
  }) => listCommits(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCommits>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListCommitsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCommits>>
>;
export type ListCommitsQueryError = ErrorType<unknown>;

/**
 * @summary List commits (filter by round or song)
 */

export function useListCommits<
  TData = Awaited<ReturnType<typeof listCommits>>,
  TError = ErrorType<unknown>,
>(
  params?: ListCommitsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCommits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCommitsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Top rising commits across all open rounds (for discovery)
 */
export const getListRisingCommitsUrl = (params?: ListRisingCommitsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/commits/rising?${stringifiedParams}`
    : `/api/commits/rising`;
};

export const listRisingCommits = async (
  params?: ListRisingCommitsParams,
  options?: RequestInit,
): Promise<CommitSummary[]> => {
  return customFetch<CommitSummary[]>(getListRisingCommitsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListRisingCommitsQueryKey = (
  params?: ListRisingCommitsParams,
) => {
  return [`/api/commits/rising`, ...(params ? [params] : [])] as const;
};

export const getListRisingCommitsQueryOptions = <
  TData = Awaited<ReturnType<typeof listRisingCommits>>,
  TError = ErrorType<unknown>,
>(
  params?: ListRisingCommitsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listRisingCommits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListRisingCommitsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listRisingCommits>>
  > = ({ signal }) => listRisingCommits(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listRisingCommits>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListRisingCommitsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listRisingCommits>>
>;
export type ListRisingCommitsQueryError = ErrorType<unknown>;

/**
 * @summary Top rising commits across all open rounds (for discovery)
 */

export function useListRisingCommits<
  TData = Awaited<ReturnType<typeof listRisingCommits>>,
  TError = ErrorType<unknown>,
>(
  params?: ListRisingCommitsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listRisingCommits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListRisingCommitsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getGetCommitUrl = (commitId: string) => {
  return `/api/commits/${commitId}`;
};

export const getCommit = async (
  commitId: string,
  options?: RequestInit,
): Promise<CommitDetail> => {
  return customFetch<CommitDetail>(getGetCommitUrl(commitId), {
    ...options,
    method: "GET",
  });
};

export const getGetCommitQueryKey = (commitId: string) => {
  return [`/api/commits/${commitId}`] as const;
};

export const getGetCommitQueryOptions = <
  TData = Awaited<ReturnType<typeof getCommit>>,
  TError = ErrorType<unknown>,
>(
  commitId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCommit>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCommitQueryKey(commitId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCommit>>> = ({
    signal,
  }) => getCommit(commitId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!commitId,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCommit>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetCommitQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCommit>>
>;
export type GetCommitQueryError = ErrorType<unknown>;

export function useGetCommit<
  TData = Awaited<ReturnType<typeof getCommit>>,
  TError = ErrorType<unknown>,
>(
  commitId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCommit>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetCommitQueryOptions(commitId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Submit a new commit to an open round (after presigned upload)
 */
export const getSubmitCommitUrl = () => {
  return `/api/commits/submit`;
};

export const submitCommit = async (
  submitCommitBody: SubmitCommitBody,
  options?: RequestInit,
): Promise<CommitDetail> => {
  return customFetch<CommitDetail>(getSubmitCommitUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(submitCommitBody),
  });
};

export const getSubmitCommitMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitCommit>>,
    TError,
    { data: BodyType<SubmitCommitBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof submitCommit>>,
  TError,
  { data: BodyType<SubmitCommitBody> },
  TContext
> => {
  const mutationKey = ["submitCommit"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof submitCommit>>,
    { data: BodyType<SubmitCommitBody> }
  > = (props) => {
    const { data } = props ?? {};

    return submitCommit(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SubmitCommitMutationResult = NonNullable<
  Awaited<ReturnType<typeof submitCommit>>
>;
export type SubmitCommitMutationBody = BodyType<SubmitCommitBody>;
export type SubmitCommitMutationError = ErrorType<unknown>;

/**
 * @summary Submit a new commit to an open round (after presigned upload)
 */
export const useSubmitCommit = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitCommit>>,
    TError,
    { data: BodyType<SubmitCommitBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof submitCommit>>,
  TError,
  { data: BodyType<SubmitCommitBody> },
  TContext
> => {
  return useMutation(getSubmitCommitMutationOptions(options));
};

/**
 * @summary List comments on a song, newest first
 */
export const getListSongCommentsUrl = (songId: string) => {
  return `/api/songs/${songId}/comments`;
};

export const listSongComments = async (
  songId: string,
  options?: RequestInit,
): Promise<Comment[]> => {
  return customFetch<Comment[]>(getListSongCommentsUrl(songId), {
    ...options,
    method: "GET",
  });
};

export const getListSongCommentsQueryKey = (songId: string) => {
  return [`/api/songs/${songId}/comments`] as const;
};

export const getListSongCommentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSongComments>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listSongComments>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListSongCommentsQueryKey(songId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listSongComments>>
  > = ({ signal }) => listSongComments(songId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!songId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listSongComments>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListSongCommentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSongComments>>
>;
export type ListSongCommentsQueryError = ErrorType<unknown>;

/**
 * @summary List comments on a song, newest first
 */

export function useListSongComments<
  TData = Awaited<ReturnType<typeof listSongComments>>,
  TError = ErrorType<unknown>,
>(
  songId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listSongComments>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListSongCommentsQueryOptions(songId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Post a comment on a song (auth required)
 */
export const getPostSongCommentUrl = (songId: string) => {
  return `/api/songs/${songId}/comments`;
};

export const postSongComment = async (
  songId: string,
  postCommentBody: PostCommentBody,
  options?: RequestInit,
): Promise<Comment> => {
  return customFetch<Comment>(getPostSongCommentUrl(songId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(postCommentBody),
  });
};

export const getPostSongCommentMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postSongComment>>,
    TError,
    { songId: string; data: BodyType<PostCommentBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postSongComment>>,
  TError,
  { songId: string; data: BodyType<PostCommentBody> },
  TContext
> => {
  const mutationKey = ["postSongComment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postSongComment>>,
    { songId: string; data: BodyType<PostCommentBody> }
  > = (props) => {
    const { songId, data } = props ?? {};

    return postSongComment(songId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostSongCommentMutationResult = NonNullable<
  Awaited<ReturnType<typeof postSongComment>>
>;
export type PostSongCommentMutationBody = BodyType<PostCommentBody>;
export type PostSongCommentMutationError = ErrorType<unknown>;

/**
 * @summary Post a comment on a song (auth required)
 */
export const usePostSongComment = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postSongComment>>,
    TError,
    { songId: string; data: BodyType<PostCommentBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof postSongComment>>,
  TError,
  { songId: string; data: BodyType<PostCommentBody> },
  TContext
> => {
  return useMutation(getPostSongCommentMutationOptions(options));
};

/**
 * @summary Delete a comment (author or admin)
 */
export const getDeleteCommentUrl = (commentId: string) => {
  return `/api/comments/${commentId}`;
};

export const deleteComment = async (
  commentId: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteCommentUrl(commentId), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteCommentMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteComment>>,
    TError,
    { commentId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteComment>>,
  TError,
  { commentId: string },
  TContext
> => {
  const mutationKey = ["deleteComment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteComment>>,
    { commentId: string }
  > = (props) => {
    const { commentId } = props ?? {};

    return deleteComment(commentId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCommentMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteComment>>
>;

export type DeleteCommentMutationError = ErrorType<unknown>;

/**
 * @summary Delete a comment (author or admin)
 */
export const useDeleteComment = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteComment>>,
    TError,
    { commentId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteComment>>,
  TError,
  { commentId: string },
  TContext
> => {
  return useMutation(getDeleteCommentMutationOptions(options));
};

/**
 * @summary Flag a comment for moderation (auth required, idempotent per user)
 */
export const getReportCommentUrl = (commentId: string) => {
  return `/api/comments/${commentId}/reports`;
};

export const reportComment = async (
  commentId: string,
  reportCommentBody: ReportCommentBody,
  options?: RequestInit,
): Promise<ReportComment201> => {
  return customFetch<ReportComment201>(getReportCommentUrl(commentId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(reportCommentBody),
  });
};

export const getReportCommentMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reportComment>>,
    TError,
    { commentId: string; data: BodyType<ReportCommentBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reportComment>>,
  TError,
  { commentId: string; data: BodyType<ReportCommentBody> },
  TContext
> => {
  const mutationKey = ["reportComment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reportComment>>,
    { commentId: string; data: BodyType<ReportCommentBody> }
  > = (props) => {
    const { commentId, data } = props ?? {};

    return reportComment(commentId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReportCommentMutationResult = NonNullable<
  Awaited<ReturnType<typeof reportComment>>
>;
export type ReportCommentMutationBody = BodyType<ReportCommentBody>;
export type ReportCommentMutationError = ErrorType<unknown>;

/**
 * @summary Flag a comment for moderation (auth required, idempotent per user)
 */
export const useReportComment = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reportComment>>,
    TError,
    { commentId: string; data: BodyType<ReportCommentBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof reportComment>>,
  TError,
  { commentId: string; data: BodyType<ReportCommentBody> },
  TContext
> => {
  return useMutation(getReportCommentMutationOptions(options));
};

/**
 * @summary Request a presigned URL to upload a file directly to storage
 */
export const getRequestUploadUrlUrl = () => {
  return `/api/storage/uploads/request-url`;
};

export const requestUploadUrl = async (
  requestUploadUrlBody: RequestUploadUrlBody,
  options?: RequestInit,
): Promise<RequestUploadUrlResponse> => {
  return customFetch<RequestUploadUrlResponse>(getRequestUploadUrlUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(requestUploadUrlBody),
  });
};

export const getRequestUploadUrlMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestUploadUrl>>,
    TError,
    { data: BodyType<RequestUploadUrlBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof requestUploadUrl>>,
  TError,
  { data: BodyType<RequestUploadUrlBody> },
  TContext
> => {
  const mutationKey = ["requestUploadUrl"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof requestUploadUrl>>,
    { data: BodyType<RequestUploadUrlBody> }
  > = (props) => {
    const { data } = props ?? {};

    return requestUploadUrl(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RequestUploadUrlMutationResult = NonNullable<
  Awaited<ReturnType<typeof requestUploadUrl>>
>;
export type RequestUploadUrlMutationBody = BodyType<RequestUploadUrlBody>;
export type RequestUploadUrlMutationError = ErrorType<unknown>;

/**
 * @summary Request a presigned URL to upload a file directly to storage
 */
export const useRequestUploadUrl = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestUploadUrl>>,
    TError,
    { data: BodyType<RequestUploadUrlBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof requestUploadUrl>>,
  TError,
  { data: BodyType<RequestUploadUrlBody> },
  TContext
> => {
  return useMutation(getRequestUploadUrlMutationOptions(options));
};

/**
 * @summary List the current user's commit drafts (notes saved before a round was open)
 */
export const getListMyDraftsUrl = () => {
  return `/api/commits/drafts`;
};

export const listMyDrafts = async (
  options?: RequestInit,
): Promise<CommitDraft[]> => {
  return customFetch<CommitDraft[]>(getListMyDraftsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListMyDraftsQueryKey = () => {
  return [`/api/commits/drafts`] as const;
};

export const getListMyDraftsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyDrafts>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyDrafts>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMyDraftsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyDrafts>>> = ({
    signal,
  }) => listMyDrafts({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyDrafts>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyDraftsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyDrafts>>
>;
export type ListMyDraftsQueryError = ErrorType<unknown>;

/**
 * @summary List the current user's commit drafts (notes saved before a round was open)
 */

export function useListMyDrafts<
  TData = Awaited<ReturnType<typeof listMyDrafts>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyDrafts>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyDraftsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Save a Note as a draft (no round required)
 */
export const getCreateDraftUrl = () => {
  return `/api/commits/drafts`;
};

export const createDraft = async (
  createDraftBody: CreateDraftBody,
  options?: RequestInit,
): Promise<CommitDraft> => {
  return customFetch<CommitDraft>(getCreateDraftUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createDraftBody),
  });
};

export const getCreateDraftMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createDraft>>,
    TError,
    { data: BodyType<CreateDraftBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createDraft>>,
  TError,
  { data: BodyType<CreateDraftBody> },
  TContext
> => {
  const mutationKey = ["createDraft"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createDraft>>,
    { data: BodyType<CreateDraftBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createDraft(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateDraftMutationResult = NonNullable<
  Awaited<ReturnType<typeof createDraft>>
>;
export type CreateDraftMutationBody = BodyType<CreateDraftBody>;
export type CreateDraftMutationError = ErrorType<unknown>;

/**
 * @summary Save a Note as a draft (no round required)
 */
export const useCreateDraft = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createDraft>>,
    TError,
    { data: BodyType<CreateDraftBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createDraft>>,
  TError,
  { data: BodyType<CreateDraftBody> },
  TContext
> => {
  return useMutation(getCreateDraftMutationOptions(options));
};

/**
 * @summary Edit a saved Note draft (any subset of fields)
 */
export const getUpdateDraftUrl = (draftId: string) => {
  return `/api/commits/drafts/${draftId}`;
};

export const updateDraft = async (
  draftId: string,
  updateDraftBody: UpdateDraftBody,
  options?: RequestInit,
): Promise<CommitDraft> => {
  return customFetch<CommitDraft>(getUpdateDraftUrl(draftId), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateDraftBody),
  });
};

export const getUpdateDraftMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateDraft>>,
    TError,
    { draftId: string; data: BodyType<UpdateDraftBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateDraft>>,
  TError,
  { draftId: string; data: BodyType<UpdateDraftBody> },
  TContext
> => {
  const mutationKey = ["updateDraft"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateDraft>>,
    { draftId: string; data: BodyType<UpdateDraftBody> }
  > = (props) => {
    const { draftId, data } = props ?? {};

    return updateDraft(draftId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateDraftMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateDraft>>
>;
export type UpdateDraftMutationBody = BodyType<UpdateDraftBody>;
export type UpdateDraftMutationError = ErrorType<unknown>;

/**
 * @summary Edit a saved Note draft (any subset of fields)
 */
export const useUpdateDraft = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateDraft>>,
    TError,
    { draftId: string; data: BodyType<UpdateDraftBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateDraft>>,
  TError,
  { draftId: string; data: BodyType<UpdateDraftBody> },
  TContext
> => {
  return useMutation(getUpdateDraftMutationOptions(options));
};

export const getDeleteDraftUrl = (draftId: string) => {
  return `/api/commits/drafts/${draftId}`;
};

export const deleteDraft = async (
  draftId: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteDraftUrl(draftId), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteDraftMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteDraft>>,
    TError,
    { draftId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteDraft>>,
  TError,
  { draftId: string },
  TContext
> => {
  const mutationKey = ["deleteDraft"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteDraft>>,
    { draftId: string }
  > = (props) => {
    const { draftId } = props ?? {};

    return deleteDraft(draftId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteDraftMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteDraft>>
>;

export type DeleteDraftMutationError = ErrorType<unknown>;

export const useDeleteDraft = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteDraft>>,
    TError,
    { draftId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteDraft>>,
  TError,
  { draftId: string },
  TContext
> => {
  return useMutation(getDeleteDraftMutationOptions(options));
};

/**
 * @summary Promote a draft into a real commit on an open round
 */
export const getSubmitDraftUrl = (draftId: string) => {
  return `/api/commits/drafts/${draftId}/submit`;
};

export const submitDraft = async (
  draftId: string,
  submitDraftBody?: SubmitDraftBody,
  options?: RequestInit,
): Promise<CommitDetail> => {
  return customFetch<CommitDetail>(getSubmitDraftUrl(draftId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(submitDraftBody),
  });
};

export const getSubmitDraftMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitDraft>>,
    TError,
    { draftId: string; data: BodyType<SubmitDraftBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof submitDraft>>,
  TError,
  { draftId: string; data: BodyType<SubmitDraftBody> },
  TContext
> => {
  const mutationKey = ["submitDraft"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof submitDraft>>,
    { draftId: string; data: BodyType<SubmitDraftBody> }
  > = (props) => {
    const { draftId, data } = props ?? {};

    return submitDraft(draftId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SubmitDraftMutationResult = NonNullable<
  Awaited<ReturnType<typeof submitDraft>>
>;
export type SubmitDraftMutationBody = BodyType<SubmitDraftBody>;
export type SubmitDraftMutationError = ErrorType<unknown>;

/**
 * @summary Promote a draft into a real commit on an open round
 */
export const useSubmitDraft = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitDraft>>,
    TError,
    { draftId: string; data: BodyType<SubmitDraftBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof submitDraft>>,
  TError,
  { draftId: string; data: BodyType<SubmitDraftBody> },
  TContext
> => {
  return useMutation(getSubmitDraftMutationOptions(options));
};

/**
 * @summary Cast an upvote on a commit (idempotent)
 */
export const getVoteOnCommitUrl = (commitId: string) => {
  return `/api/commits/${commitId}/vote`;
};

export const voteOnCommit = async (
  commitId: string,
  options?: RequestInit,
): Promise<VoteResult> => {
  return customFetch<VoteResult>(getVoteOnCommitUrl(commitId), {
    ...options,
    method: "POST",
  });
};

export const getVoteOnCommitMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof voteOnCommit>>,
    TError,
    { commitId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof voteOnCommit>>,
  TError,
  { commitId: string },
  TContext
> => {
  const mutationKey = ["voteOnCommit"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof voteOnCommit>>,
    { commitId: string }
  > = (props) => {
    const { commitId } = props ?? {};

    return voteOnCommit(commitId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type VoteOnCommitMutationResult = NonNullable<
  Awaited<ReturnType<typeof voteOnCommit>>
>;

export type VoteOnCommitMutationError = ErrorType<unknown>;

/**
 * @summary Cast an upvote on a commit (idempotent)
 */
export const useVoteOnCommit = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof voteOnCommit>>,
    TError,
    { commitId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof voteOnCommit>>,
  TError,
  { commitId: string },
  TContext
> => {
  return useMutation(getVoteOnCommitMutationOptions(options));
};

/**
 * @summary Remove an upvote
 */
export const getUnvoteCommitUrl = (commitId: string) => {
  return `/api/commits/${commitId}/vote`;
};

export const unvoteCommit = async (
  commitId: string,
  options?: RequestInit,
): Promise<VoteResult> => {
  return customFetch<VoteResult>(getUnvoteCommitUrl(commitId), {
    ...options,
    method: "DELETE",
  });
};

export const getUnvoteCommitMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof unvoteCommit>>,
    TError,
    { commitId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof unvoteCommit>>,
  TError,
  { commitId: string },
  TContext
> => {
  const mutationKey = ["unvoteCommit"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof unvoteCommit>>,
    { commitId: string }
  > = (props) => {
    const { commitId } = props ?? {};

    return unvoteCommit(commitId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UnvoteCommitMutationResult = NonNullable<
  Awaited<ReturnType<typeof unvoteCommit>>
>;

export type UnvoteCommitMutationError = ErrorType<unknown>;

/**
 * @summary Remove an upvote
 */
export const useUnvoteCommit = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof unvoteCommit>>,
    TError,
    { commitId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof unvoteCommit>>,
  TError,
  { commitId: string },
  TContext
> => {
  return useMutation(getUnvoteCommitMutationOptions(options));
};

/**
 * @summary Log a stem or file download (optional auth)
 */
export const getLogDownloadUrl = () => {
  return `/api/downloads/log`;
};

export const logDownload = async (
  logDownloadBody: LogDownloadBody,
  options?: RequestInit,
): Promise<LogDownload200> => {
  return customFetch<LogDownload200>(getLogDownloadUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(logDownloadBody),
  });
};

export const getLogDownloadMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logDownload>>,
    TError,
    { data: BodyType<LogDownloadBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof logDownload>>,
  TError,
  { data: BodyType<LogDownloadBody> },
  TContext
> => {
  const mutationKey = ["logDownload"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof logDownload>>,
    { data: BodyType<LogDownloadBody> }
  > = (props) => {
    const { data } = props ?? {};

    return logDownload(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type LogDownloadMutationResult = NonNullable<
  Awaited<ReturnType<typeof logDownload>>
>;
export type LogDownloadMutationBody = BodyType<LogDownloadBody>;
export type LogDownloadMutationError = ErrorType<unknown>;

/**
 * @summary Log a stem or file download (optional auth)
 */
export const useLogDownload = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logDownload>>,
    TError,
    { data: BodyType<LogDownloadBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof logDownload>>,
  TError,
  { data: BodyType<LogDownloadBody> },
  TContext
> => {
  return useMutation(getLogDownloadMutationOptions(options));
};

/**
 * @summary Public platform stats for landing page
 */
export const getGetPublicStatsUrl = () => {
  return `/api/stats/public`;
};

export const getPublicStats = async (
  options?: RequestInit,
): Promise<PublicStats> => {
  return customFetch<PublicStats>(getGetPublicStatsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetPublicStatsQueryKey = () => {
  return [`/api/stats/public`] as const;
};

export const getGetPublicStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPublicStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getPublicStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPublicStatsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicStats>>> = ({
    signal,
  }) => getPublicStats({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPublicStats>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetPublicStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPublicStats>>
>;
export type GetPublicStatsQueryError = ErrorType<unknown>;

/**
 * @summary Public platform stats for landing page
 */

export function useGetPublicStats<
  TData = Awaited<ReturnType<typeof getPublicStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getPublicStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetPublicStatsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Admin dashboard stats
 */
export const getGetAdminStatsUrl = () => {
  return `/api/stats/admin`;
};

export const getAdminStats = async (
  options?: RequestInit,
): Promise<AdminStats> => {
  return customFetch<AdminStats>(getGetAdminStatsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAdminStatsQueryKey = () => {
  return [`/api/stats/admin`] as const;
};

export const getGetAdminStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAdminStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAdminStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAdminStatsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminStats>>> = ({
    signal,
  }) => getAdminStats({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAdminStats>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAdminStats>>
>;
export type GetAdminStatsQueryError = ErrorType<unknown>;

/**
 * @summary Admin dashboard stats
 */

export function useGetAdminStats<
  TData = Awaited<ReturnType<typeof getAdminStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAdminStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetAdminStatsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getAdminListSongsUrl = () => {
  return `/api/admin/songs`;
};

export const adminListSongs = async (
  options?: RequestInit,
): Promise<Song[]> => {
  return customFetch<Song[]>(getAdminListSongsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getAdminListSongsQueryKey = () => {
  return [`/api/admin/songs`] as const;
};

export const getAdminListSongsQueryOptions = <
  TData = Awaited<ReturnType<typeof adminListSongs>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof adminListSongs>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAdminListSongsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListSongs>>> = ({
    signal,
  }) => adminListSongs({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof adminListSongs>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type AdminListSongsQueryResult = NonNullable<
  Awaited<ReturnType<typeof adminListSongs>>
>;
export type AdminListSongsQueryError = ErrorType<unknown>;

export function useAdminListSongs<
  TData = Awaited<ReturnType<typeof adminListSongs>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof adminListSongs>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getAdminListSongsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getAdminCreateSongUrl = () => {
  return `/api/admin/songs`;
};

export const adminCreateSong = async (
  createSongBody: CreateSongBody,
  options?: RequestInit,
): Promise<Song> => {
  return customFetch<Song>(getAdminCreateSongUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createSongBody),
  });
};

export const getAdminCreateSongMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminCreateSong>>,
    TError,
    { data: BodyType<CreateSongBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminCreateSong>>,
  TError,
  { data: BodyType<CreateSongBody> },
  TContext
> => {
  const mutationKey = ["adminCreateSong"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminCreateSong>>,
    { data: BodyType<CreateSongBody> }
  > = (props) => {
    const { data } = props ?? {};

    return adminCreateSong(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminCreateSongMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminCreateSong>>
>;
export type AdminCreateSongMutationBody = BodyType<CreateSongBody>;
export type AdminCreateSongMutationError = ErrorType<unknown>;

export const useAdminCreateSong = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminCreateSong>>,
    TError,
    { data: BodyType<CreateSongBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminCreateSong>>,
  TError,
  { data: BodyType<CreateSongBody> },
  TContext
> => {
  return useMutation(getAdminCreateSongMutationOptions(options));
};

export const getAdminUpdateSongUrl = (songId: string) => {
  return `/api/admin/songs/${songId}`;
};

export const adminUpdateSong = async (
  songId: string,
  updateSongBody: UpdateSongBody,
  options?: RequestInit,
): Promise<Song> => {
  return customFetch<Song>(getAdminUpdateSongUrl(songId), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateSongBody),
  });
};

export const getAdminUpdateSongMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminUpdateSong>>,
    TError,
    { songId: string; data: BodyType<UpdateSongBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminUpdateSong>>,
  TError,
  { songId: string; data: BodyType<UpdateSongBody> },
  TContext
> => {
  const mutationKey = ["adminUpdateSong"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminUpdateSong>>,
    { songId: string; data: BodyType<UpdateSongBody> }
  > = (props) => {
    const { songId, data } = props ?? {};

    return adminUpdateSong(songId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminUpdateSongMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminUpdateSong>>
>;
export type AdminUpdateSongMutationBody = BodyType<UpdateSongBody>;
export type AdminUpdateSongMutationError = ErrorType<unknown>;

export const useAdminUpdateSong = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminUpdateSong>>,
    TError,
    { songId: string; data: BodyType<UpdateSongBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminUpdateSong>>,
  TError,
  { songId: string; data: BodyType<UpdateSongBody> },
  TContext
> => {
  return useMutation(getAdminUpdateSongMutationOptions(options));
};

export const getAdminAdvanceSongPhaseUrl = (songId: string) => {
  return `/api/admin/songs/${songId}/advance-phase`;
};

export const adminAdvanceSongPhase = async (
  songId: string,
  advancePhaseBody: AdvancePhaseBody,
  options?: RequestInit,
): Promise<Song> => {
  return customFetch<Song>(getAdminAdvanceSongPhaseUrl(songId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(advancePhaseBody),
  });
};

export const getAdminAdvanceSongPhaseMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminAdvanceSongPhase>>,
    TError,
    { songId: string; data: BodyType<AdvancePhaseBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminAdvanceSongPhase>>,
  TError,
  { songId: string; data: BodyType<AdvancePhaseBody> },
  TContext
> => {
  const mutationKey = ["adminAdvanceSongPhase"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminAdvanceSongPhase>>,
    { songId: string; data: BodyType<AdvancePhaseBody> }
  > = (props) => {
    const { songId, data } = props ?? {};

    return adminAdvanceSongPhase(songId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminAdvanceSongPhaseMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminAdvanceSongPhase>>
>;
export type AdminAdvanceSongPhaseMutationBody = BodyType<AdvancePhaseBody>;
export type AdminAdvanceSongPhaseMutationError = ErrorType<unknown>;

export const useAdminAdvanceSongPhase = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminAdvanceSongPhase>>,
    TError,
    { songId: string; data: BodyType<AdvancePhaseBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminAdvanceSongPhase>>,
  TError,
  { songId: string; data: BodyType<AdvancePhaseBody> },
  TContext
> => {
  return useMutation(getAdminAdvanceSongPhaseMutationOptions(options));
};

export const getAdminAddSongFileUrl = (songId: string) => {
  return `/api/admin/songs/${songId}/files`;
};

export const adminAddSongFile = async (
  songId: string,
  adminAddSongFileBody: AdminAddSongFileBody,
  options?: RequestInit,
): Promise<SongFile> => {
  return customFetch<SongFile>(getAdminAddSongFileUrl(songId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(adminAddSongFileBody),
  });
};

export const getAdminAddSongFileMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminAddSongFile>>,
    TError,
    { songId: string; data: BodyType<AdminAddSongFileBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminAddSongFile>>,
  TError,
  { songId: string; data: BodyType<AdminAddSongFileBody> },
  TContext
> => {
  const mutationKey = ["adminAddSongFile"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminAddSongFile>>,
    { songId: string; data: BodyType<AdminAddSongFileBody> }
  > = (props) => {
    const { songId, data } = props ?? {};

    return adminAddSongFile(songId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminAddSongFileMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminAddSongFile>>
>;
export type AdminAddSongFileMutationBody = BodyType<AdminAddSongFileBody>;
export type AdminAddSongFileMutationError = ErrorType<unknown>;

export const useAdminAddSongFile = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminAddSongFile>>,
    TError,
    { songId: string; data: BodyType<AdminAddSongFileBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminAddSongFile>>,
  TError,
  { songId: string; data: BodyType<AdminAddSongFileBody> },
  TContext
> => {
  return useMutation(getAdminAddSongFileMutationOptions(options));
};

export const getAdminCreateRoundUrl = () => {
  return `/api/admin/rounds`;
};

export const adminCreateRound = async (
  createRoundBody: CreateRoundBody,
  options?: RequestInit,
): Promise<Round> => {
  return customFetch<Round>(getAdminCreateRoundUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createRoundBody),
  });
};

export const getAdminCreateRoundMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminCreateRound>>,
    TError,
    { data: BodyType<CreateRoundBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminCreateRound>>,
  TError,
  { data: BodyType<CreateRoundBody> },
  TContext
> => {
  const mutationKey = ["adminCreateRound"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminCreateRound>>,
    { data: BodyType<CreateRoundBody> }
  > = (props) => {
    const { data } = props ?? {};

    return adminCreateRound(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminCreateRoundMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminCreateRound>>
>;
export type AdminCreateRoundMutationBody = BodyType<CreateRoundBody>;
export type AdminCreateRoundMutationError = ErrorType<unknown>;

export const useAdminCreateRound = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminCreateRound>>,
    TError,
    { data: BodyType<CreateRoundBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminCreateRound>>,
  TError,
  { data: BodyType<CreateRoundBody> },
  TContext
> => {
  return useMutation(getAdminCreateRoundMutationOptions(options));
};

export const getAdminUpdateRoundUrl = (roundId: string) => {
  return `/api/admin/rounds/${roundId}`;
};

export const adminUpdateRound = async (
  roundId: string,
  updateRoundBody: UpdateRoundBody,
  options?: RequestInit,
): Promise<Round> => {
  return customFetch<Round>(getAdminUpdateRoundUrl(roundId), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateRoundBody),
  });
};

export const getAdminUpdateRoundMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminUpdateRound>>,
    TError,
    { roundId: string; data: BodyType<UpdateRoundBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminUpdateRound>>,
  TError,
  { roundId: string; data: BodyType<UpdateRoundBody> },
  TContext
> => {
  const mutationKey = ["adminUpdateRound"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminUpdateRound>>,
    { roundId: string; data: BodyType<UpdateRoundBody> }
  > = (props) => {
    const { roundId, data } = props ?? {};

    return adminUpdateRound(roundId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminUpdateRoundMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminUpdateRound>>
>;
export type AdminUpdateRoundMutationBody = BodyType<UpdateRoundBody>;
export type AdminUpdateRoundMutationError = ErrorType<unknown>;

export const useAdminUpdateRound = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminUpdateRound>>,
    TError,
    { roundId: string; data: BodyType<UpdateRoundBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminUpdateRound>>,
  TError,
  { roundId: string; data: BodyType<UpdateRoundBody> },
  TContext
> => {
  return useMutation(getAdminUpdateRoundMutationOptions(options));
};

export const getAdminListCommitsUrl = (params?: AdminListCommitsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/admin/commits?${stringifiedParams}`
    : `/api/admin/commits`;
};

export const adminListCommits = async (
  params?: AdminListCommitsParams,
  options?: RequestInit,
): Promise<CommitSummary[]> => {
  return customFetch<CommitSummary[]>(getAdminListCommitsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getAdminListCommitsQueryKey = (
  params?: AdminListCommitsParams,
) => {
  return [`/api/admin/commits`, ...(params ? [params] : [])] as const;
};

export const getAdminListCommitsQueryOptions = <
  TData = Awaited<ReturnType<typeof adminListCommits>>,
  TError = ErrorType<unknown>,
>(
  params?: AdminListCommitsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof adminListCommits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAdminListCommitsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof adminListCommits>>
  > = ({ signal }) => adminListCommits(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof adminListCommits>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type AdminListCommitsQueryResult = NonNullable<
  Awaited<ReturnType<typeof adminListCommits>>
>;
export type AdminListCommitsQueryError = ErrorType<unknown>;

export function useAdminListCommits<
  TData = Awaited<ReturnType<typeof adminListCommits>>,
  TError = ErrorType<unknown>,
>(
  params?: AdminListCommitsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof adminListCommits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getAdminListCommitsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const getAdminSetCommitStatusUrl = (commitId: string) => {
  return `/api/admin/commits/${commitId}/status`;
};

export const adminSetCommitStatus = async (
  commitId: string,
  adminSetCommitStatusBody: AdminSetCommitStatusBody,
  options?: RequestInit,
): Promise<CommitDetail> => {
  return customFetch<CommitDetail>(getAdminSetCommitStatusUrl(commitId), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(adminSetCommitStatusBody),
  });
};

export const getAdminSetCommitStatusMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminSetCommitStatus>>,
    TError,
    { commitId: string; data: BodyType<AdminSetCommitStatusBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminSetCommitStatus>>,
  TError,
  { commitId: string; data: BodyType<AdminSetCommitStatusBody> },
  TContext
> => {
  const mutationKey = ["adminSetCommitStatus"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminSetCommitStatus>>,
    { commitId: string; data: BodyType<AdminSetCommitStatusBody> }
  > = (props) => {
    const { commitId, data } = props ?? {};

    return adminSetCommitStatus(commitId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminSetCommitStatusMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminSetCommitStatus>>
>;
export type AdminSetCommitStatusMutationBody =
  BodyType<AdminSetCommitStatusBody>;
export type AdminSetCommitStatusMutationError = ErrorType<unknown>;

export const useAdminSetCommitStatus = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminSetCommitStatus>>,
    TError,
    { commitId: string; data: BodyType<AdminSetCommitStatusBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminSetCommitStatus>>,
  TError,
  { commitId: string; data: BodyType<AdminSetCommitStatusBody> },
  TContext
> => {
  return useMutation(getAdminSetCommitStatusMutationOptions(options));
};

export const getAdminCreateSongCreditUrl = (songId: string) => {
  return `/api/admin/songs/${songId}/credits`;
};

export const adminCreateSongCredit = async (
  songId: string,
  createSongCreditBody: CreateSongCreditBody,
  options?: RequestInit,
): Promise<SongCredit> => {
  return customFetch<SongCredit>(getAdminCreateSongCreditUrl(songId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createSongCreditBody),
  });
};

export const getAdminCreateSongCreditMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminCreateSongCredit>>,
    TError,
    { songId: string; data: BodyType<CreateSongCreditBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminCreateSongCredit>>,
  TError,
  { songId: string; data: BodyType<CreateSongCreditBody> },
  TContext
> => {
  const mutationKey = ["adminCreateSongCredit"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminCreateSongCredit>>,
    { songId: string; data: BodyType<CreateSongCreditBody> }
  > = (props) => {
    const { songId, data } = props ?? {};

    return adminCreateSongCredit(songId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminCreateSongCreditMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminCreateSongCredit>>
>;
export type AdminCreateSongCreditMutationBody = BodyType<CreateSongCreditBody>;
export type AdminCreateSongCreditMutationError = ErrorType<unknown>;

export const useAdminCreateSongCredit = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminCreateSongCredit>>,
    TError,
    { songId: string; data: BodyType<CreateSongCreditBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminCreateSongCredit>>,
  TError,
  { songId: string; data: BodyType<CreateSongCreditBody> },
  TContext
> => {
  return useMutation(getAdminCreateSongCreditMutationOptions(options));
};

export const getAdminReorderSongCreditsUrl = (songId: string) => {
  return `/api/admin/songs/${songId}/credits/reorder`;
};

export const adminReorderSongCredits = async (
  songId: string,
  reorderSongCreditsBody: ReorderSongCreditsBody,
  options?: RequestInit,
): Promise<SongCredit[]> => {
  return customFetch<SongCredit[]>(getAdminReorderSongCreditsUrl(songId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(reorderSongCreditsBody),
  });
};

export const getAdminReorderSongCreditsMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminReorderSongCredits>>,
    TError,
    { songId: string; data: BodyType<ReorderSongCreditsBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminReorderSongCredits>>,
  TError,
  { songId: string; data: BodyType<ReorderSongCreditsBody> },
  TContext
> => {
  const mutationKey = ["adminReorderSongCredits"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminReorderSongCredits>>,
    { songId: string; data: BodyType<ReorderSongCreditsBody> }
  > = (props) => {
    const { songId, data } = props ?? {};

    return adminReorderSongCredits(songId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminReorderSongCreditsMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminReorderSongCredits>>
>;
export type AdminReorderSongCreditsMutationBody =
  BodyType<ReorderSongCreditsBody>;
export type AdminReorderSongCreditsMutationError = ErrorType<unknown>;

export const useAdminReorderSongCredits = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminReorderSongCredits>>,
    TError,
    { songId: string; data: BodyType<ReorderSongCreditsBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminReorderSongCredits>>,
  TError,
  { songId: string; data: BodyType<ReorderSongCreditsBody> },
  TContext
> => {
  return useMutation(getAdminReorderSongCreditsMutationOptions(options));
};

export const getAdminUpdateSongCreditUrl = (creditId: string) => {
  return `/api/admin/credits/${creditId}`;
};

export const adminUpdateSongCredit = async (
  creditId: string,
  updateSongCreditBody: UpdateSongCreditBody,
  options?: RequestInit,
): Promise<SongCredit> => {
  return customFetch<SongCredit>(getAdminUpdateSongCreditUrl(creditId), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateSongCreditBody),
  });
};

export const getAdminUpdateSongCreditMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminUpdateSongCredit>>,
    TError,
    { creditId: string; data: BodyType<UpdateSongCreditBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminUpdateSongCredit>>,
  TError,
  { creditId: string; data: BodyType<UpdateSongCreditBody> },
  TContext
> => {
  const mutationKey = ["adminUpdateSongCredit"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminUpdateSongCredit>>,
    { creditId: string; data: BodyType<UpdateSongCreditBody> }
  > = (props) => {
    const { creditId, data } = props ?? {};

    return adminUpdateSongCredit(creditId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminUpdateSongCreditMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminUpdateSongCredit>>
>;
export type AdminUpdateSongCreditMutationBody = BodyType<UpdateSongCreditBody>;
export type AdminUpdateSongCreditMutationError = ErrorType<unknown>;

export const useAdminUpdateSongCredit = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminUpdateSongCredit>>,
    TError,
    { creditId: string; data: BodyType<UpdateSongCreditBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminUpdateSongCredit>>,
  TError,
  { creditId: string; data: BodyType<UpdateSongCreditBody> },
  TContext
> => {
  return useMutation(getAdminUpdateSongCreditMutationOptions(options));
};

export const getAdminDeleteSongCreditUrl = (creditId: string) => {
  return `/api/admin/credits/${creditId}`;
};

export const adminDeleteSongCredit = async (
  creditId: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getAdminDeleteSongCreditUrl(creditId), {
    ...options,
    method: "DELETE",
  });
};

export const getAdminDeleteSongCreditMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminDeleteSongCredit>>,
    TError,
    { creditId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminDeleteSongCredit>>,
  TError,
  { creditId: string },
  TContext
> => {
  const mutationKey = ["adminDeleteSongCredit"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminDeleteSongCredit>>,
    { creditId: string }
  > = (props) => {
    const { creditId } = props ?? {};

    return adminDeleteSongCredit(creditId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminDeleteSongCreditMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminDeleteSongCredit>>
>;

export type AdminDeleteSongCreditMutationError = ErrorType<unknown>;

export const useAdminDeleteSongCredit = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminDeleteSongCredit>>,
    TError,
    { creditId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminDeleteSongCredit>>,
  TError,
  { creditId: string },
  TContext
> => {
  return useMutation(getAdminDeleteSongCreditMutationOptions(options));
};

/**
 * @summary Publish a new official version with merged commits
 */
export const getAdminCreateVersionUrl = () => {
  return `/api/admin/versions`;
};

export const adminCreateVersion = async (
  adminCreateVersionBody: AdminCreateVersionBody,
  options?: RequestInit,
): Promise<VersionWithMerges> => {
  return customFetch<VersionWithMerges>(getAdminCreateVersionUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(adminCreateVersionBody),
  });
};

export const getAdminCreateVersionMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminCreateVersion>>,
    TError,
    { data: BodyType<AdminCreateVersionBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminCreateVersion>>,
  TError,
  { data: BodyType<AdminCreateVersionBody> },
  TContext
> => {
  const mutationKey = ["adminCreateVersion"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminCreateVersion>>,
    { data: BodyType<AdminCreateVersionBody> }
  > = (props) => {
    const { data } = props ?? {};

    return adminCreateVersion(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminCreateVersionMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminCreateVersion>>
>;
export type AdminCreateVersionMutationBody = BodyType<AdminCreateVersionBody>;
export type AdminCreateVersionMutationError = ErrorType<unknown>;

/**
 * @summary Publish a new official version with merged commits
 */
export const useAdminCreateVersion = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminCreateVersion>>,
    TError,
    { data: BodyType<AdminCreateVersionBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminCreateVersion>>,
  TError,
  { data: BodyType<AdminCreateVersionBody> },
  TContext
> => {
  return useMutation(getAdminCreateVersionMutationOptions(options));
};

/**
 * Layers the song's current official mix together with each selected
commit's audio into a single mp3, uploads it to object storage, and
returns the entity path. The returned `objectPath` can be used as
`officialMixObjectPath` when publishing a new version. Curators can
load the result in an `<audio>` element to preview before confirming
the publish. On any mixing failure the endpoint responds 502 so the
admin UI can fall back to the existing manual upload flow.

 * @summary Auto-mix a song's current version with selected commit stems
 */
export const getAdminPreviewVersionMixUrl = () => {
  return `/api/admin/versions/preview-mix`;
};

export const adminPreviewVersionMix = async (
  adminPreviewVersionMixBody: AdminPreviewVersionMixBody,
  options?: RequestInit,
): Promise<AdminPreviewVersionMixResponse> => {
  return customFetch<AdminPreviewVersionMixResponse>(
    getAdminPreviewVersionMixUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(adminPreviewVersionMixBody),
    },
  );
};

export const getAdminPreviewVersionMixMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminPreviewVersionMix>>,
    TError,
    { data: BodyType<AdminPreviewVersionMixBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminPreviewVersionMix>>,
  TError,
  { data: BodyType<AdminPreviewVersionMixBody> },
  TContext
> => {
  const mutationKey = ["adminPreviewVersionMix"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminPreviewVersionMix>>,
    { data: BodyType<AdminPreviewVersionMixBody> }
  > = (props) => {
    const { data } = props ?? {};

    return adminPreviewVersionMix(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminPreviewVersionMixMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminPreviewVersionMix>>
>;
export type AdminPreviewVersionMixMutationBody =
  BodyType<AdminPreviewVersionMixBody>;
export type AdminPreviewVersionMixMutationError = ErrorType<void>;

/**
 * @summary Auto-mix a song's current version with selected commit stems
 */
export const useAdminPreviewVersionMix = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminPreviewVersionMix>>,
    TError,
    { data: BodyType<AdminPreviewVersionMixBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminPreviewVersionMix>>,
  TError,
  { data: BodyType<AdminPreviewVersionMixBody> },
  TContext
> => {
  return useMutation(getAdminPreviewVersionMixMutationOptions(options));
};

/**
 * @summary List comments with at least one user-submitted report
 */
export const getAdminListCommentReportsUrl = () => {
  return `/api/admin/comments/reports`;
};

export const adminListCommentReports = async (
  options?: RequestInit,
): Promise<ReportedComment[]> => {
  return customFetch<ReportedComment[]>(getAdminListCommentReportsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getAdminListCommentReportsQueryKey = () => {
  return [`/api/admin/comments/reports`] as const;
};

export const getAdminListCommentReportsQueryOptions = <
  TData = Awaited<ReturnType<typeof adminListCommentReports>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof adminListCommentReports>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAdminListCommentReportsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof adminListCommentReports>>
  > = ({ signal }) => adminListCommentReports({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof adminListCommentReports>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type AdminListCommentReportsQueryResult = NonNullable<
  Awaited<ReturnType<typeof adminListCommentReports>>
>;
export type AdminListCommentReportsQueryError = ErrorType<unknown>;

/**
 * @summary List comments with at least one user-submitted report
 */

export function useAdminListCommentReports<
  TData = Awaited<ReturnType<typeof adminListCommentReports>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof adminListCommentReports>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getAdminListCommentReportsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}
